import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { WINDOW } from '@campus/browser';
import { AppShellUIService, NavigationRailItemInterface } from '@campus/components';
import { Permissions } from '@campus/dal';
import { EnvironmentWebsiteInterface, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { PermissionService } from '@campus/shared';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppViewModel } from '../../app.viewmodel';

@Component({
  standalone: false,
  selector: 'campus-polpo-rail',
  templateUrl: './app-rail.component.html',
})
export class AppRailComponent implements OnInit, OnDestroy {
  @HostBinding('class') defaultClasses = ['flex', 'flex-column', 'gap-m', 'items-center', 'h-full'];

  public FeaturePermissions = Permissions.feature;

  public hasAppShellDarkModePermission$: Observable<boolean>;

  public navigationItems$: Observable<NavigationRailItemInterface[]>;
  public activeRouterLink$: Observable<string[]>;
  public isDesktop$: Observable<boolean>;
  public menuClosed$: Observable<boolean>;

  private router: Router = inject(Router);
  private window: Window = inject(WINDOW);
  private viewModel: AppViewModel = inject(AppViewModel);
  private permissionService: PermissionService = inject(PermissionService);
  private environmentWebsite: EnvironmentWebsiteInterface = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private appShellService: AppShellUIService = inject(AppShellUIService);

  private _destroy$ = new Subject<void>();

  public ngOnInit() {
    this.hasAppShellDarkModePermission$ = this.getHasAppShellDarkModePermission$();

    this.navigationItems$ = this.getNavigationItems$();
    this.activeRouterLink$ = this.getActiveItem$();
    this.isDesktop$ = this.appShellService.isDesktop$;
    this.menuClosed$ = this.appShellService.openMenu$.pipe(map((open) => !open));
  }

  public ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openMenu() {
    this.appShellService.openMenu();
  }

  public onThemeChange(event: boolean) {
    document.body[event ? 'setAttribute' : 'removeAttribute']('data-theme', 'dark');
  }

  public onNavClick(event: NavigationRailItemInterface) {
    if (event.routerLink[0] === 'faq') {
      this.window.open(this.environmentWebsite.faqUrl, '_blank');
    }
  }

  private getNavigationItems$(): Observable<NavigationRailItemInterface[]> {
    return this.viewModel.sideNavItems$.pipe(
      map((items) => {
        return items.map((item) => {
          return {
            label: item.title,
            icon: item.icon,
            routerLink: Array.isArray(item.link) ? item.link : [item.link],
            badge: item.badge,
          };
        });
      })
    );
  }

  private getActiveItem$(): Observable<string[]> {
    return this.navigationItems$.pipe(
      map(
        (items) =>
          items
            .filter((item) => item.routerLink.every((i) => i != null))
            .find((item) => this.router.url.includes(item.routerLink.join('/')))?.routerLink
      )
    );
  }

  private getHasAppShellDarkModePermission$(): Observable<boolean> {
    return this.permissionService.hasPermission$(this.FeaturePermissions.APP_SHELL_DARK_MODE);
  }
}
